<script setup lang="ts">
import { EmbeddedDropdownTransition, useClickOutside, globalOverlayContainer } from '#shared'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, onBeforeMount, onBeforeUnmount, onUpdated, ref, useTemplateRef, watch } from 'vue'
import { ConversationsDropdown } from '#widgets/conversations-dropdown'
import { personalChatsQueryOptions } from '#entities/chat'

const conversationsToggleButton = document.querySelector<HTMLElement>('[data-behavior~="chat/conversations-toggle"]')
const isConversationsShown = ref(false)

const personalChatsQuery = useInfiniteQuery({ ...personalChatsQueryOptions })
const personalChats = computed(() => personalChatsQuery.data.value?.pages.flatMap(page => page.data) ?? [])
const unreadChatCount = computed(() => personalChats.value.filter(chat => (chat.unreadCount ?? 0) > 0).length)

onUpdated(() => {
  if (unreadChatCount.value === 0 && !personalChatsQuery.isPending.value && personalChatsQuery.hasNextPage.value) {
    personalChatsQuery.fetchNextPage()
  }
})

watch(
  unreadChatCount,
  unreadChatCount => {
    if (!conversationsToggleButton) return

    if (unreadChatCount > 0) {
      conversationsToggleButton.classList.add('has-new-messages')
      conversationsToggleButton.dataset.notificationCount = unreadChatCount.toString()
    } else {
      conversationsToggleButton.classList.remove('has-new-messages')
    }
  },
  { immediate: true }
)

const onConversationsToggle = () => (isConversationsShown.value = !isConversationsShown.value)
const onConversationsClose = () => (isConversationsShown.value = false)
onBeforeMount(() => {
  conversationsToggleButton?.addEventListener('click', onConversationsToggle)
})
onBeforeUnmount(() => {
  conversationsToggleButton?.removeEventListener('click', onConversationsToggle)
})

const conversationsDropdown = useTemplateRef('conversationsDropdown')
useClickOutside(
  computed(() => [conversationsToggleButton, conversationsDropdown.value?.$el]),
  () => (isConversationsShown.value = false)
)
</script>

<template>
  <Teleport :to="globalOverlayContainer">
    <EmbeddedDropdownTransition>
      <ConversationsDropdown
        v-if="isConversationsShown && conversationsToggleButton"
        :toggleEl="conversationsToggleButton"
        ref="conversationsDropdown"
        @close="onConversationsClose"
      />
    </EmbeddedDropdownTransition>
  </Teleport>
</template>

<style lang="scss">
@use '#mixins/notification' as notification;
@use '#mixins/button' as button;

[data-behavior~='chat/conversations-toggle'] {
  @include button.reset;

  & {
    background-color: transparent;
    color: white;
  }

  &.has-new-messages {
    @include notification.regular;
  }
}
</style>
